// components/Mouvements.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOrganisations } from "../Auth/useOrganisations";
import { toast } from "react-toastify";
import GifLoader from "../Components/loader";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getMenusreadyToServe, getMouvements } from "../Services/MenuApi";
import moment from "moment";
import { getUsers } from "../Services/UserApi";
import { getImageUrl } from "../Helpers/Helpers";
import ProductSelector from "../Components/Caisse/ProductSelector";

const Mouvements = () => {
  const { t } = useTranslation();
  const [organisations, , usedOrganisations] = useOrganisations();
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  // Filtres
  const [filters, setFilters] = useState({
    produit_id: "",
    type_mouvement: "",
    sens: "",
    user_id: "",
    date_debut: moment().startOf("day").format("YYYY-MM-DD"),
    date_fin: moment().endOf("day").format("YYYY-MM-DD"),
    page: 1,
    limit: 50,
  });

  // Données pour les filtres
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [users, setUsers] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableSens, setAvailableSens] = useState([]);
  const loadUsers = useCallback(async () => {
    if (!usedOrganisations) return;
    try {
      const response = await getUsers(usedOrganisations);
      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    }
  }, [usedOrganisations]);
  // Données de pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  // Statistiques
  const [stats, setStats] = useState({
    total_entrees: 0,
    total_sorties: 0,
    total_montant: 0,
    count: 0,
    total_ventes: 0,
    quantite_vendue: 0,
    montant_ventes: 0,
  });

  const [statsByType, setStatsByType] = useState([]);
  const [statsByDay, setStatsByDay] = useState([]);
  const [topProducts, setTopProducts] = useState([]);

  useEffect(() => {
    if (usedOrganisations) {
      loadMouvements();
      loadUsers();
    }
  }, [usedOrganisations]);

  useEffect(() => {
    if (usedOrganisations) {
      loadMouvements();
    }
  }, [usedOrganisations, filters.page]);

  // const loadFilterData = async () => {
  //   try {
  //     // Charger les produits
  //     const productsRes = await getMenusreadyToServe(usedOrganisations);
  //     if (productsRes.data.success) {
  //       setProducts(productsRes.data.data);
  //     }

  //     // Charger les utilisateurs
  //     //   const usersRes = await getUsersForFilter(usedOrganisations);
  //     //   if (usersRes.success) {
  //     //     setUsers(usersRes.data);
  //     //   }
  //   } catch (error) {
  //     toast.error("Erreur lors du chargement des données de filtre");
  //   }
  // };

  const loadMouvements = async () => {
    try {
      setLoading(true);

      // Préparer les filtres pour l'API
      const apiFilters = {
        ...filters,
        start_date: filters.start_date
          ? filters.start_date.toISOString().split("T")[0]
          : null,
        end_date: filters.end_date
          ? filters.end_date.toISOString().split("T")[0]
          : null,
        page: filters.page,
        limit: filters.limit,
      };

      // Enlever les filtres vides
      Object.keys(apiFilters).forEach((key) => {
        if (!apiFilters[key] && apiFilters[key] !== 0) {
          delete apiFilters[key];
        }
      });

      const response = await getMouvements(usedOrganisations, apiFilters);

      if (response.success) {
        setMouvements(response.data);
        setPagination(response.pagination);
        setAvailableTypes(response.filters?.types || []);
        setAvailableSens(response.filters?.sens || []);
        setStats(response.totals);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des mouvements");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    handleFilterChange("produit_id", product?._id || "");
  };
  const handleResetFilters = () => {
    setFilters({
      produit_id: "",
      type_mouvement: "",
      sens: "",
      user_id: "",
      date_debut: moment().startOf("day").format("YYYY-MM-DD"),
      date_fin: moment().endOf("day").format("YYYY-MM-DD"),
      page: 1,
      limit: 50,
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const getSensColor = (sens) => {
    return sens === "entrée" ? "success" : "danger";
  };

  const getTypeBadge = (type) => {
    const colors = {
      vente: "primary",
      achat: "success",
      inventaire: "warning",
      retour: "info",
      perte: "danger",
      ajustement: "secondary",
    };

    return colors[type] || "secondary";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportToExcel = () => {
    // Simple export functionality - you can enhance this with a proper Excel library
    const headers = [
      "Date",
      "Produit",
      "Type",
      "Sens",
      "Quantité",
      "Prix",
      "Montant",
      "Utilisateur",
      "Notes",
    ];
    const data = mouvements.map((m) => [
      formatDate(m.date_mouvement),
      m.produit_id?.titre?.fr || "N/A",
      m.type_mouvement,
      m.sens,
      m.quantite,
      m.prix_unitaire ? `${m.prix_unitaire.toFixed(2)} DT` : "N/A",
      m.montant_total ? `${Math.abs(m.montant_total).toFixed(2)} DT` : "N/A",
      m.user_id?.nom || m.caissier_id?.nom || "N/A",
      m.notes || "",
    ]);

    const csvContent = [headers, ...data]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mouvements_stock_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
  };

  return (
    <section className="content">
      <GifLoader loading={loading || statsLoading} />

      <div className="row">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              {/* Header */}
              {/* <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h4 className="box-title">
                    <i className="fa fa-exchange-alt mr-2"></i>
                    {t("Mouvements de Stock")}
                  </h4>
                </div>
                <div>
                  <button
                    className="btn btn-success mr-2"
                    onClick={exportToExcel}
                  >
                    <i className="fa fa-file-export mr-2"></i>
                    {t("Exporter")}
                  </button>
                </div>
              </div> */}

              {/* Cartes de Ventes */}
           

              {/* Filtres */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="box">
                    <div className="box-body">
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>{t("Produit")}</label>
                            <ProductSelector
                              organisation={usedOrganisations}
                              selectedProduct={selectedProduct}
                              onProductSelect={handleProductSelect}
                            />
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="form-group">
                            <label>{t("Type")}</label>
                            <select
                              className="form-control"
                              value={filters.type_mouvement}
                              onChange={(e) =>
                                handleFilterChange(
                                  "type_mouvement",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">{t("Tous les types")}</option>
                              {availableTypes.map((type) => (
                                <option key={type} value={type}>
                                  {t(
                                    type.charAt(0).toUpperCase() +
                                      type.slice(1),
                                  )}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="form-group">
                            <label>{t("Sens")}</label>
                            <select
                              className="form-control"
                              value={filters.sens}
                              onChange={(e) =>
                                handleFilterChange("sens", e.target.value)
                              }
                            >
                              <option value="">{t("Tous")}</option>
                              {availableSens.map((sens) => (
                                <option key={sens} value={sens}>
                                  {t(
                                    sens.charAt(0).toUpperCase() +
                                      sens.slice(1),
                                  )}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="form-group">
                            <label>{t("Utilisateur")}</label>
                            <select
                              className="form-control"
                              value={filters.user_id}
                              onChange={(e) =>
                                handleFilterChange("user_id", e.target.value)
                              }
                            >
                              <option value="">
                                {t("Tous les utilisateurs")}
                              </option>
                              {users.map((user) => (
                                <option key={user._id} value={user._id}>
                                  {user.nom} {user.prenom}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="form-group">
                            <label>{t("Limite")}</label>
                            <select
                              className="form-control"
                              value={filters.limit}
                              onChange={(e) =>
                                handleFilterChange(
                                  "limit",
                                  parseInt(e.target.value),
                                )
                              }
                            >
                              <option value="20">20</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                              <option value="200">200</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>{t("Date de début")}</label>
                            <DatePicker
                              className="form-control"
                              selected={
                                filters.date_debut
                                  ? moment(filters.date_debut).toDate()
                                  : null
                              }
                              onChange={(date) =>
                                handleFilterChange(
                                  "date_debut",
                                  date ? moment(date).format("YYYY-MM-DD") : "",
                                )
                              }
                              selectsStart
                              startDate={
                                filters.date_debut
                                  ? moment(filters.date_debut).toDate()
                                  : null
                              }
                              endDate={
                                filters.date_fin
                                  ? moment(filters.date_fin).toDate()
                                  : null
                              }
                              dateFormat="dd-MM-yyyy"
                              placeholderText={t("Date de début")}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>{t("Date de fin")}</label>
                            <DatePicker
                              className="form-control"
                              selected={
                                filters.date_fin
                                  ? moment(filters.date_fin).toDate()
                                  : null
                              }
                              onChange={(date) =>
                                handleFilterChange(
                                  "date_fin",
                                  date ? moment(date).format("YYYY-MM-DD") : "",
                                )
                              }
                              selectsEnd
                              startDate={
                                filters.date_debut
                                  ? moment(filters.date_debut).toDate()
                                  : null
                              }
                              endDate={
                                filters.date_fin
                                  ? moment(filters.date_fin).toDate()
                                  : null
                              }
                              minDate={
                                filters.date_debut
                                  ? moment(filters.date_debut).toDate()
                                  : null
                              }
                              dateFormat="dd-MM-yyyy"
                              placeholderText={t("Date de fin")}
                            />
                          </div>
                        </div>

                        <div className="col-md-6 d-flex align-items-center">
                          <div className="form-group mb-0">
                            <button
                              className="btn btn-secondary mr-2"
                              onClick={handleResetFilters}
                            >
                              <i className="fa fa-redo mr-2"></i>
                            </button>

                            <button
                              className="btn btn-danger"
                              onClick={() => {
                                setFilters((prev) => ({ ...prev, page: 1 }));
                                loadMouvements();
                              }}
                            >
                              <i className="fa fa-search mr-2"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistiques par Type */}
              {statsByType.length > 0 && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="box">
                      <div className="box-body">
                        <h5 className="box-title">
                          <i className="fa fa-chart-pie mr-2"></i>
                          {t("Statistiques par Type")}
                        </h5>

                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th>{t("Type")}</th>
                                <th>{t("Nombre")}</th>
                                <th>{t("Quantité Totale")}</th>
                                <th>{t("Montant Total")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statsByType.map((stat, index) => (
                                <tr key={index}>
                                  <td>
                                    <span
                                      className={`badge badge-${getTypeBadge(
                                        stat._id,
                                      )}`}
                                    >
                                      {t(
                                        stat._id.charAt(0).toUpperCase() +
                                          stat._id.slice(1),
                                      )}
                                    </span>
                                  </td>
                                  <td>{stat.count}</td>
                                  <td>{stat.total_quantite}</td>
                                  <td>
                                    {stat.total_montant?.toFixed(2) || "0.00"}{" "}
                                    DT
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
   <div className="row mb-4">
                <div className="col-xl-3 col-lg-6 col-12">
                  <div className="box">
                    <div className="box-body">
                      <div className="d-flex align-items-center">
                        <div
                          className="me-15 mr-2 bg-primary rounded-circle h-50 w-50 l-h-60 text-center"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <span className="text-white font-size-20">
                            <i className="fa fa-receipt"></i>
                          </span>
                        </div>
                        <div>
                          <h4 className="mb-0">{stats.total_ventes || 0}</h4>
                          <p className="text-fade mb-0">
                            {t("Nombre de Ventes")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 col-12">
                  <div className="box">
                    <div className="box-body">
                      <div className="d-flex align-items-center">
                        <div
                          className="me-15 mr-2 bg-info rounded-circle h-50 w-50 l-h-60 text-center"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <span className="text-white font-size-20">
                            <i className="fa fa-cubes"></i>
                          </span>
                        </div>
                        <div>
                          <h4 className="mb-0">{stats.quantite_vendue || 0}</h4>
                          <p className="text-fade mb-0">
                            {t("Quantité Vendue")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 col-12">
                  <div className="box">
                    <div className="box-body">
                      <div className="d-flex align-items-center">
                        <div
                          className="me-15 mr-2 bg-success rounded-circle h-50 w-50 l-h-60 text-center"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <span className="text-white font-size-20">
                            <i className="fa fa-money-bill"></i>
                          </span>
                        </div>
                        <div>
                          <h4 className="mb-0">
                            {stats.montant_ventes?.toFixed(2) || "0.00"} DT
                          </h4>
                          <p className="text-fade mb-0">
                            {t("Chiffre d'Affaires")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 col-12">
                  <div className="box">
                    <div className="box-body">
                      <div className="d-flex align-items-center">
                        <div
                          className="me-15 mr-2 bg-warning rounded-circle h-50 w-50 l-h-60 text-center"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <span className="text-white font-size-20">
                            <i className="fa fa-calculator"></i>
                          </span>
                        </div>
                        <div>
                          <h4 className="mb-0">
                            {stats.total_ventes
                              ? (
                                  stats.montant_ventes / stats.total_ventes
                                ).toFixed(2)
                              : "0.00"}{" "}
                            DT
                          </h4>
                          <p className="text-fade mb-0">{t("Panier Moyen")}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Tableau des Mouvements */}
              <div className="row">
                <div className="col-12">
                  <div className="box">
                    <div className="box-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              {" "}
                              <th>{t("Code")}</th>
                              <th>{t("Date")}</th>
                              <th>{t("Produit")}</th>
                              <th>{t("Type")}</th>
                              {/* <th>{t("Emplacement")}</th> */}
                              <th>{t("Sens")}</th>
                              <th>{t("Quantité")}</th>
                              <th>{t("Unité")}</th>
                              <th>{t("Prix Unitaire")}</th>
                              <th>{t("Montant")}</th>
                              <th>{t("Utilisateur")}</th>
                              <th>{t("Notes")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mouvements.length > 0 ? (
                              mouvements.map((mouvement) => (
                                <tr key={mouvement._id}>
                                  <td>
                                    {mouvement.code ? (
                                      <code>{mouvement.code}</code>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td>
                                    {formatDate(mouvement.date_mouvement)}
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {mouvement.produit_id?.image?.[0] ? (
                                        <img
                                          src={getImageUrl(
                                            mouvement.produit_id.image,
                                          )}
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src =
                                              "images/product/no-image.png";
                                          }}
                                          alt={mouvement.produit_id.titre?.fr}
                                          className="rounded mr-2"
                                          style={{
                                            width: "40px",
                                            height: "40px",
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : null}
                                      <div>
                                        <strong>
                                          {mouvement.produit_id?.titre?.fr}
                                        </strong>
                                        {mouvement.produit_id?.reference && (
                                          <div>
                                            <small className="text-muted">
                                              {mouvement.produit_id.reference}
                                            </small>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge badge-${getTypeBadge(
                                        mouvement.type_mouvement,
                                      )}`}
                                    >
                                      {t(
                                        mouvement.type_mouvement
                                          .charAt(0)
                                          .toUpperCase() +
                                          mouvement.type_mouvement.slice(1),
                                      )}
                                    </span>
                                  </td>
                                  {/* <td>
                                    {mouvement.type_mouvement === "stock"
                                      ? mouvement.emplacement
                                      : "-"}
                                  </td> */}
                                  <td>
                                    <span
                                      className={`badge badge-${getSensColor(
                                        mouvement?.sens,
                                      )}`}
                                    >
                                      {t(
                                        mouvement?.sens
                                          ?.charAt(0)
                                          .toUpperCase() +
                                          mouvement?.sens?.slice(1),
                                      )}
                                    </span>
                                  </td>
                                  <td>
                                    <strong
                                      className={`text-${getSensColor(
                                        mouvement.sens,
                                      )}`}
                                    >
                                      {mouvement.sens === "entrée" ? "+" : "-"}
                                      {mouvement.quantite}
                                    </strong>
                                  </td>
                                  <td>{mouvement.unite}</td>
                                  <td>
                                    {mouvement.prix_unitaire
                                      ? `${mouvement.prix_unitaire.toFixed(
                                          2,
                                        )} DT`
                                      : "-"}
                                  </td>
                                  <td>
                                    {mouvement.montant_total
                                      ? `${Math.abs(
                                          mouvement.montant_total,
                                        ).toFixed(2)} DT`
                                      : "-"}
                                  </td>
                                  <td>
                                    {mouvement.user_id ? (
                                      <div>
                                        {mouvement.user_id.nom}{" "}
                                        {mouvement.user_id.prenom}
                                      </div>
                                    ) : mouvement.caissier_id ? (
                                      <div>
                                        {mouvement.caissier_id.nom}{" "}
                                        {mouvement.caissier_id.prenom}
                                        <div>
                                          <small className="text-muted">
                                            (Caissier)
                                          </small>
                                        </div>
                                      </div>
                                    ) : mouvement.serveur_id ? (
                                      <div>
                                        {mouvement.serveur_id.nom}{" "}
                                        {mouvement.serveur_id.prenom}
                                        <div>
                                          <small className="text-muted">
                                            (Serveur)
                                          </small>
                                        </div>
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td>
                                    {mouvement.notes &&
                                    mouvement.type_mouvement !== "vente" ? (
                                      <small title={mouvement.notes}>
                                        {mouvement.notes.length > 50
                                          ? `${mouvement.notes.substring(
                                              0,
                                              50,
                                            )}...`
                                          : mouvement.notes}
                                      </small>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="10" className="text-center py-5">
                                  <i className="fa fa-exchange-alt fa-3x text-muted mb-3"></i>
                                  <h5>{t("Aucun mouvement trouvé")}</h5>
                                  <p className="text-muted">
                                    {t(
                                      "Aucun mouvement ne correspond aux filtres appliqués",
                                    )}
                                  </p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="box">
                      <div className="box-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="text-muted">
                              {t("Affichage de")}{" "}
                              {(pagination.page - 1) * pagination.limit + 1}
                              {" à "}
                              {Math.min(
                                pagination.page * pagination.limit,
                                pagination.total,
                              )}
                              {" sur "}
                              {pagination.total} {t("mouvements")}
                            </span>
                          </div>

                          <nav>
                            <ul className="pagination mb-0">
                              <li
                                className={`page-item ${
                                  pagination.page === 1 ? "disabled" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    handlePageChange(pagination.page - 1)
                                  }
                                  disabled={pagination.page === 1}
                                >
                                  <i className="fa fa-chevron-left"></i>
                                </button>
                              </li>

                              {[...Array(pagination.totalPages)].map((_, i) => {
                                const pageNum = i + 1;
                                // Show first, last, and pages around current
                                if (
                                  pageNum === 1 ||
                                  pageNum === pagination.totalPages ||
                                  (pageNum >= pagination.page - 2 &&
                                    pageNum <= pagination.page + 2)
                                ) {
                                  return (
                                    <li
                                      key={pageNum}
                                      className={`page-item ${
                                        pagination.page === pageNum
                                          ? "active"
                                          : ""
                                      }`}
                                    >
                                      <button
                                        className="page-link"
                                        onClick={() =>
                                          handlePageChange(pageNum)
                                        }
                                      >
                                        {pageNum}
                                      </button>
                                    </li>
                                  );
                                } else if (
                                  pageNum === pagination.page - 3 ||
                                  pageNum === pagination.page + 3
                                ) {
                                  return (
                                    <li
                                      key={pageNum}
                                      className="page-item disabled"
                                    >
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }
                                return null;
                              })}

                              <li
                                className={`page-item ${
                                  pagination.page === pagination.totalPages
                                    ? "disabled"
                                    : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    handlePageChange(pagination.page + 1)
                                  }
                                  disabled={
                                    pagination.page === pagination.totalPages
                                  }
                                >
                                  <i className="fa fa-chevron-right"></i>
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Mouvements;
